import React, { useState, useCallback, forwardRef, useImperativeHandle } from "react";

import axios from "axios";

import { API_URL } from "shared/config/apiRoutes";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";
import { normalizeOutcomesToArray } from "shared/components/vega/LumenV2/utils";

import { Message, VeroCopilotProps } from "./types";
import { VeroCopilotPanel } from "./VeroCopilotPanel";

// Simple unique ID generator to avoid uuid package issues
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export interface VeroCopilotRef {
  open: () => void;
  close: () => void;
}

export const VeroCopilot = forwardRef<VeroCopilotRef, VeroCopilotProps>(({
  dealId,
  dealData,
  onSendMessage: customMessageHandler,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const veroCopilot = useVeroFlag(VeroFlag.veroCopilot);

  // Don't render if feature flag is disabled
  if (!veroCopilot) {
    return null;
  }

  // Don't render if no valid dealId and no custom handler
  if (
    !customMessageHandler &&
    (!dealId || typeof dealId !== "string" || dealId.trim() === "")
  ) {
    // Component won't render without a dealId or custom handler
    return null;
  }

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Expose open/close methods via ref
  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }), [handleOpen, handleClose]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      // Add user message
      const userMessage: Message = {
        id: generateId(),
        content,
        sender: "user",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        let response: string;

        if (customMessageHandler) {
          // Use custom handler if provided
          response = await customMessageHandler(content);
        } else if (
          dealId &&
          typeof dealId === "string" &&
          dealId.trim() !== ""
        ) {
          // Build chat history from current messages (excluding the one we just added)
          // Only send the last 20 messages to prevent payload/token limits
          const MAX_HISTORY_MESSAGES = 20;
          const recentMessages = messages.slice(-MAX_HISTORY_MESSAGES);
          const chatHistory = recentMessages.map((msg) => ({
            role: msg.sender === "user" ? "user" : "assistant",
            content: msg.content,
          }));
          // Build enriched context from dealData
          const enrichedContext: any = {
            dealStatus: dealData?.dealStatus,
            applicationVrn: dealData?.applicationVrn,
          };

          // Include Lumen results if available (streamlined but comprehensive)
          if (dealData?.lumenRun) {
            // Helper to extract ALL formula results dynamically
            const extractFormulaResults = (history: any[]) => {
              if (!history || !Array.isArray(history)) return {};

              const taggedFormulas: any = {};
              const calculatedValues: any = {};

              // Track the last computed value to associate with tags
              let lastComputedValue: any;
              const pendingTagAssignment: string | null = null;

              history.forEach((item: any, index: number) => {
                // First, check if this item has a result we should track (Lumen uses 'result' not 'value')
                if ("result" in item && !item.formula?.startsWith("#")) {
                  lastComputedValue = item.result;
                }

                // Extract ALL tagged formulas (those starting with #)
                if (item.formula && item.formula.startsWith("#")) {
                  // For tagged formulas, extract both the tag name AND the result
                  const colonIndex = item.formula.indexOf(":");
                  let tagName: string;

                  if (colonIndex > -1) {
                    // Formula has embedded expression after colon (e.g., #income-to-rent:((value) / rent))
                    tagName = item.formula.substring(1, colonIndex);

                    // For formulas with embedded expressions, look ahead for the computed value
                    // Often the next item or items compute the embedded expression
                    if (!("result" in item) && index < history.length - 1) {
                      // Look at the next few items for a related computation
                      for (
                        let i = index + 1;
                        i < Math.min(index + 3, history.length);
                        i++
                      ) {
                        const nextItem = history[i];
                        if (
                          "result" in nextItem &&
                          nextItem.formula?.includes(tagName)
                        ) {
                          taggedFormulas[tagName] = nextItem.result;
                          break;
                        }
                      }
                    }
                  } else {
                    // Simple tagged formula without embedded expression
                    tagName = item.formula.substring(1);
                  }

                  // If we haven't already set a value from look-ahead
                  if (!(tagName in taggedFormulas)) {
                    // Store the actual value from the result field (Lumen uses 'result' not 'value')
                    let extractedValue = null;

                    if ("result" in item) {
                      extractedValue = item.result;
                    } else if ("value" in item) {
                      extractedValue = item.value;
                    } else if ("score" in item) {
                      extractedValue = item.score;
                    } else if ("scoreLabel" in item) {
                      extractedValue = item.scoreLabel;
                    } else if ("status" in item) {
                      extractedValue = item.status;
                    } else if (
                      lastComputedValue !== undefined &&
                      (tagName.includes("total") ||
                        tagName.includes("income") ||
                        tagName.includes("cash"))
                    ) {
                      // For value-based tags, use the last computed value if it seems related
                      extractedValue = lastComputedValue;
                      lastComputedValue = undefined; // Reset after use
                    }

                    // Convert monetary values from cents to dollars
                    // Skip conversion for ratios, booleans, and other non-monetary values
                    if (
                      extractedValue !== null &&
                      typeof extractedValue === "number" &&
                      (tagName.includes("income") ||
                        tagName.includes("cash") ||
                        tagName.includes("asset") ||
                        tagName.includes("rent") ||
                        tagName.includes("total-verified"))
                    ) {
                      // Skip conversion for ratios, booleans, and conditional checks
                      if (
                        !tagName.includes("ratio") &&
                        !tagName.includes("ideal") &&
                        !tagName.includes("conditional") &&
                        !tagName.includes("to-rent")
                      ) {
                        // income-to-rent is a ratio (0.002), not a monetary value
                        // Only convert actual monetary amounts
                        extractedValue /= 100;
                      }
                    }

                    taggedFormulas[tagName] = extractedValue;
                  }
                }
                // Also capture raw calculated values
                else if (item.formula) {
                  // Capture all values from result field (Lumen uses 'result' not 'value')
                  const hasValue = "result" in item || "value" in item;

                  if (hasValue) {
                    // Get the value from result field preferentially (Lumen uses 'result')
                    let value =
                      item.result !== undefined ? item.result : item.value;

                    // Convert monetary values from cents to dollars for specific formulas
                    // The backend expects dollars and tells the LLM all values are in dollars,
                    // but Lumen stores values in cents, so we convert here to match backend expectations
                    const isMonetaryFormula =
                      item.formula === "application.rent" ||
                      item.formula === "application.maxIncome" ||
                      item.formula.includes("sumBy(incomeStreams") ||
                      item.formula.includes("sum(incomes)") ||
                      item.formula.includes("sum(legacyAssets)") ||
                      item.formula.includes("sumBy(flatten(assets") ||
                      item.formula.includes("sumBy(assets") ||
                      item.formula.includes("rent * ") ||
                      item.formula.includes("rent*");

                    if (isMonetaryFormula && typeof value === "number") {
                      value /= 100; // Convert cents to dollars
                    }

                    // Simple property accesses
                    if (item.formula.match(/^[a-zA-Z]+\.[a-zA-Z]+$/)) {
                      calculatedValues[item.formula] = value;
                    }
                    // Income calculations
                    else if (item.formula.includes("sumBy(incomeStreams")) {
                      calculatedValues.totalIncomeCalculation = value;
                    }
                    // Asset calculations
                    else if (
                      item.formula.includes("sumBy(flatten(assets)") ||
                      item.formula.includes("sumBy(assets")
                    ) {
                      calculatedValues.totalAssetsCalculation = value;
                    }
                    // Sum operations
                    else if (item.formula.includes("sum(incomes)")) {
                      calculatedValues.sumIncomes = value;
                    } else if (item.formula.includes("sum(legacyAssets)")) {
                      calculatedValues.sumLegacyAssets = value;
                    }
                    // Capture rent multiplier calculations
                    else if (
                      item.formula.includes("rent * ") ||
                      item.formula.includes("rent*")
                    ) {
                      calculatedValues.rentMultiplier = value;
                    }
                    // Capture division operations (often ratios)
                    else if (item.formula.includes(" / ")) {
                      // Keep the raw formula result with a descriptive key
                      if (item.formula.includes("12")) {
                        calculatedValues.monthlyCalculation = value;
                      } else if (
                        item.formula.includes("income") &&
                        item.formula.includes("rent")
                      ) {
                        calculatedValues.incomeToRentRatio = value;
                      } else if (
                        item.formula.includes("assets") &&
                        item.formula.includes("rent")
                      ) {
                        calculatedValues.assetsToRentRatio = value;
                      }
                    }
                    // Capture filter/reject/isEmpty operations (usually validation checks)
                    else if (
                      item.formula.includes("isEmpty(") ||
                      item.formula.includes("filter(") ||
                      item.formula.includes("reject(")
                    ) {
                      // These often return arrays or booleans for validation
                      if (value !== null && value !== undefined) {
                        const key = item.formula.includes("isEmpty")
                          ? "validationCheck"
                          : "filteredData";
                        calculatedValues[
                          `${key}_${Object.keys(calculatedValues).length}`
                        ] = value;
                      }
                    }
                  }
                }
              });

              // Group tagged formulas by prefix for better organization
              const groupedFormulas: any = {};
              Object.keys(taggedFormulas).forEach((key) => {
                const prefix = key.split("-")[0]; // Get the prefix (income, credit, criminal, etc.)
                if (!groupedFormulas[prefix]) {
                  groupedFormulas[prefix] = {};
                }
                groupedFormulas[prefix][key] = taggedFormulas[key];
              });

              return {
                grouped: groupedFormulas,
                all: taggedFormulas,
                values: calculatedValues,
              };
            };

            enrichedContext.lumenData = {
              // Overall deal scores
              score: dealData.lumenRun.score,
              scoreLabel: dealData.lumenRun.scoreLabel,
              scoreLabelColor: dealData.lumenRun.scoreLabelColor,

              // Important note for LLM - all monetary values have been converted from cents to dollars
              monetaryUnits: "USD_DOLLARS",
              monetaryNote:
                "All monetary values in this lumenData section are in US dollars (converted from cents)",

              // Key outcomes and recommendations
              outcomes: normalizeOutcomesToArray(dealData.lumenRun.outcomes).map((outcome: any) => ({
                type: outcome.type,
                message: outcome.message,
                severity: outcome.severity,
                formula: outcome.formula,
                name: outcome.name,
              })),
              recommendations: dealData.lumenRun.recommendations,

              // Extract evaluation results for each applicant
              evaluationResults: dealData.lumenRun.results?.map(
                (result: any) => {
                  const formulaExtraction = extractFormulaResults(
                    result.evaluation?.history
                  );

                  return {
                    applicantId: result.applicantId || result.applicant,
                    score: result.evaluation?.score,
                    scoreLabel: result.evaluation?.scoreLabel,

                    // All formula results organized dynamically
                    formulaTags: formulaExtraction.all, // Flat list of all tagged formulas
                    formulaGroups: formulaExtraction.grouped, // Grouped by prefix (income-, credit-, etc.)
                    calculatedValues: formulaExtraction.values, // Raw calculated values from formulas

                    // Summary of what was evaluated
                    evaluationSummary: {
                      totalFormulasTested: Object.keys(formulaExtraction.all)
                        .length,
                      groupsEvaluated: Object.keys(formulaExtraction.grouped),
                      hasIncomeChecks: "income" in formulaExtraction.grouped,
                      hasCreditChecks: "credit" in formulaExtraction.grouped,
                      hasCriminalChecks:
                        "criminal" in formulaExtraction.grouped,
                      hasRentalChecks: "rental" in formulaExtraction.grouped,
                      hasBackgroundChecks:
                        "background" in formulaExtraction.grouped,
                    },
                  };
                }
              ),
            };
          }

          // Include applicants summary if available (streamlined)
          if (dealData?.applicants) {
            enrichedContext.applicants = dealData.applicants.map(
              (applicant: any) => ({
                name: `${applicant.firstName || ""} ${
                  applicant.lastName || ""
                }`.trim(),
                party: applicant.party,
                backgroundCheckNeeded:
                  applicant.shouldRunBackgroundAndCreditChecks,
              })
            );
          }

          // Include key application metrics (aggregated)
          if (dealData?.apps && dealData.apps.length > 0) {
            const { apps } = dealData;
            const incomes = apps
              .map((app: any) => app.info?.monthlyIncome)
              .filter(Boolean);
            const creditScores = apps
              .map((app: any) => app.info?.creditScore)
              .filter(Boolean);

            enrichedContext.applicationSummary = {
              totalApplications: apps.length,
              submittedCount: apps.filter((app: any) => app.info?.isSubmitted)
                .length,
              totalMonthlyIncome: incomes.reduce(
                (sum: number, income: number) => sum + income,
                0
              ),
              averageCreditScore:
                creditScores.length > 0
                  ? Math.round(
                      creditScores.reduce(
                        (sum: number, score: number) => sum + score,
                        0
                      ) / creditScores.length
                    )
                  : null,
              // Note: totalMonthlyIncome is in dollars (converted from cents in dealData)
            };
          }

          // Call the VERO Copilot API endpoint with enriched context and chat history
          // Use camelCase to match API conventions
          const requestPayload = {
            message: content,
            dealId: dealId.trim(),
            context: enrichedContext,
            chatHistory: chatHistory, // Send conversation history
          };

          const { data } = await axios.post(
            `${API_URL}/copilot/chat`,
            requestPayload,
            // @ts-ignore
            { convertToSnakeCase: false } // Bypass automatic snake_case conversion
          );

          response = data.response;
        } else {
          // Fallback if no dealId is provided
          response =
            "I need a deal context to provide helpful insights. Please open a specific deal to use VERO Copilot.";
        }

        // Add assistant message
        const assistantMessage: Message = {
          id: generateId(),
          content: response,
          sender: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (error) {
        console.error("Error sending message:", error);

        let errorContent = "Sorry, I encountered an error. Please try again.";

        // Handle specific error cases
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 429) {
            errorContent =
              "You've reached your daily message limit. Please try again tomorrow.";
          } else if (error.response?.status === 404) {
            errorContent =
              "Deal not found. Please make sure you have a valid deal selected.";
          } else if (error.response?.data?.error) {
            errorContent = error.response.data.error;
          }
        }

        // Add error message
        const errorMessage: Message = {
          id: generateId(),
          content: errorContent,
          sender: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [dealId, dealData, isOpen, customMessageHandler, messages]
  );

  return (
    <VeroCopilotPanel
      isOpen={isOpen}
      onClose={handleClose}
      messages={messages}
      onSendMessage={handleSendMessage}
      onClearMessages={() => setMessages([])}
      isLoading={isLoading}
    />
  );
});
